import React, { useEffect, useState } from 'react';

import { useScreenDetector } from "@/hooks/useScreenDetector.ts";
import { CollapseList } from "../../ui/DataDisplay/CollapseList/CollapseList.tsx";
import { cartTwoIcon } from "../../assets/icons/icons.ts";
import { DeliveryPoint as Point } from "../../types/Delivery.interface.ts";
import { formatBranchNameInProductCard } from "@/utils/formatters/formatBranchName.ts";
import { SortInterface } from '../../ui/SortInterface/SortInterface.tsx';
import { RootState, useAppSelector } from '@/utils/storage/stores/store.ts';

interface Props {
    point: Point
    showCount?: number,
    addProductToCart: (cartId: number, itemId: number) => void,
    cardRef: React.RefObject<HTMLDivElement>,
}

interface OfferProps {
    offer: {
        id: number,
        date: string,
        price: number
    },
    isMobile: boolean,
    addProductToCart: (cartId: number, itemId: number) => void,
}

//Преобразование даты из строкового формата в числовой
const parseDate = (date: string) => {
    const match = date.match(/^(\d{1,2}):(\d{2})\s(\d{1,2})\.(\d{1,2})$/);
    if (!match) return null;
    const [_, hours, minutes, day, month] = match.map(Number);
    return new Date(new Date().getFullYear(), month - 1, day, hours, minutes);
};

/**
 * Компонент отображения оффера
 * @param {OfferProps} offer - Оффер
 * @param {boolean} isMobile - Флаг мобильного устройства
 * @param {number} cartId - ID корзины
 * @param {Function} addProductToCart - Функция добавления товара в корзину
 * @returns {React.FC<OfferProps>} Компонент отображения оффера
 */
const Offer: React.FC<OfferProps> = ({
    offer,
    isMobile,
    addProductToCart,
}) => {
    const cartId = useAppSelector((state: RootState) => state.user.userInfo?.cartId as number);
    const [time, _] = offer.date.split(' ');
    const parsedDate = parseDate(offer.date);
    const dateDay = parsedDate?.getDate();
    const today = new Date().getDate();
    const diff = dateDay ? (dateDay - today) : -1;
    const isToday = diff === 0;
    const isTomorrow = diff === 1;

    const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];

    let dateText = '';

    if (isToday) {
        dateText = `Сегодня в ${time}`;
    } else if (isTomorrow) {
        dateText = `Завтра в ${time}`;
    } else {
        dateText = `${dateDay} ${MONTHS[parsedDate?.getMonth() || 0]} в ${time}`;
    }


    return (
        <div className='flex items-center py-2 border-b border-gray-200 last:border-b-0'>
            <p className='w-1/2 text-base'>{dateText}</p>
            <p className='w-1/4 font-bold text-center text-lg'>{offer.price}</p>
            <div className='w-1/4 flex justify-end'>
                <button className="inline-flex items-center justify-center gap-2 whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-9 bg-[#F39200] hover:bg-orange-500 rounded-lg px-6 py-1 text-base font-bold text-white"
                    id={`add-to-cart-btn-${offer.id}`}
                    onClick={() => addProductToCart(cartId, offer.id)}>
                    {!isMobile && <>В корзину</>}
                    {isMobile && <img src={cartTwoIcon} alt='В корзину' />}
                </button>
            </div>
        </div>
    );
};

/**
 * Компонент отображения точки доставки
 * @param {Point} point - Точка доставки
 * @param {number} cartId - ID корзины
 * @param {Function} addProductToCart - Функция добавления товара в корзину
 * @param {React.RefObject<HTMLDivElement>} cardRef - Ссылка на компонент корзины
 * @param {number} showCount - Количество отображаемых офферов
 * @returns {React.FC<Props>} Компонент отображения точки доставки
 */
export const DeliveryPoint: React.FC<Props> = ({
    point,
    addProductToCart,
    cardRef,
    showCount = 3
}) => {
    const currentTownId = useAppSelector((state: RootState) => state.user.userInfo?.townId || 1);
    const { isMobile } = useScreenDetector();
    const [orderTypePrice, setOrderTypePrice] = useState<'asc' | 'desc'>('asc');
    const [orderTypeDate, setOrderTypeDate] = useState<'asc' | 'desc'>('asc');
    const [sortedOffers, setSortedOffers] = useState(point.offers);

    //Отслеживание изменения типа сортировки цены
    useEffect(() => {
        sortByPrice();
    }, [orderTypePrice]);

    //Отслеживание изменения типа сортировки даты
    useEffect(() => {
        sortByDate();
    }, [orderTypeDate]);

    //Изменение типа сортировки даты
    const changeOrderTypeDate = (newType: 'asc' | 'desc') => {
        setOrderTypeDate(() => newType);
        // sortOffers(orderTypePrice, newType);
    };

    //Изменение типа сортировки цены
    const changeOrderTypePrice = (newType: 'asc' | 'desc') => {
        setOrderTypePrice(() => newType);
        // sortOffers(newType, orderTypeDate);
    };

    //Сортировка по цене
    const sortByPrice = () => {
        const sortedOffers = [...point.offers.sort(compareByPrice)];
        setSortedOffers(() => sortedOffers);
    };

    //Сравнение по цене
    const compareByPrice = (a: { date: string, price: number }, b: { date: string, price: number }, stopPropagation: boolean = false) => {
        switch (orderTypePrice) {
            case 'asc':
                return a.price - b.price;
            case 'desc':
                return b.price - a.price;
            default:
                return stopPropagation ? 0 : compareByDate(a, b, true);
        }
    };

    //Сортировка по дате
    const sortByDate = () => {
        const sortedOffers = [...point.offers.sort(compareByDate)];
        setSortedOffers(() => sortedOffers);
    };

    //Сравнение по дате
    const compareByDate = (a: { date: string, price: number }, b: { date: string, price: number }, stopPropagation: boolean = false): number => {
        switch (orderTypeDate) {
            case 'asc':
                return ascSortByDate(a.date, b.date);
            case 'desc':
                return descSortByDate(a.date, b.date);
            default:
                return stopPropagation ? 0 : compareByPrice(a, b, true);
        }
    };

    //Сортировка по дате по возрастанию
    const ascSortByDate = (a: string, b: string) => {
        const timeA = parseDate(a)?.getTime();
        const timeB = parseDate(b)?.getTime();
        return (timeA! - timeB!) || 0;
    };

    //Сортировка по дате по убыванию
    const descSortByDate = (a: string, b: string) => {
        const timeA = parseDate(a)?.getTime();
        const timeB = parseDate(b)?.getTime();
        return (timeB! - timeA!) || 0;
    };

    return (
        <div className='flex-1 sm:pl-8 flex flex-col justify-start'>
            <div className='flex items-center border-b border-gray-200 font-semibold text-base mb-2 mt-0'>
                <div className='w-1/2 flex items-center'>
                    <span className='font-normal text-sm'>
                        {formatBranchNameInProductCard('Доставка в ПВ ', { townId: currentTownId, point: { townId: point.townId, address: point.name } })}
                    </span>
                    <SortInterface currentOrderType={orderTypeDate} changeOrderType={changeOrderTypeDate} />
                </div>
                <div className='w-1/4 flex items-center justify-center'>
                    <span>Цена</span>
                    <SortInterface currentOrderType={orderTypePrice} changeOrderType={changeOrderTypePrice} />
                </div>
                <div className='w-1/4'></div>
            </div>
            <CollapseList showCount={showCount} showButton={true} cardRef={cardRef}>
                {sortedOffers.map(offer => (<Offer
                    offer={offer}
                    isMobile={isMobile}
                    addProductToCart={addProductToCart}
                />))}
            </CollapseList>
        </div>
    );
};
